
import React, { MouseEvent, useEffect, useState, useRef } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import { useWindowSize } from 'react-use';

import { currentBasicTextFilterPropsAtom, currentFormNameAtom, currentUserSessionAtom, isSaveLoadingAtom } from 'library/store';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';

import { useMutation, useQuery, useQueryClient } from 'react-query';

import { useTheme } from '@mui/material/styles';

import { MenuItem, Card, CardContent, IconButton, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Checkbox } from "@mui/material";


import PrintIcon from "@mui/icons-material/Print";
import VisibilityIcon from '@mui/icons-material/Visibility';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';


import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { flexBetween, flexBetweenCenter, flexCenter, justifyCenter, typographyGroupBoxStyling } from 'themes/commonStyles';

import useSchoolYearService, { useBasicFilterSchoolYear } from 'features/setup/services/SchoolYear';
import useRegistrationService from 'features/production/services/Registration';

import { ISchoolYear, ISchoolYearClass, ISchoolYearClassTemplate } from 'features/setup/models/SchoolYear';

import { BasicTextFilterForm, defaultBasicTextFilterProps } from 'components/ui/BasicTextFilterForm';

import useEntityService from 'features/services/Entity';

import { Button } from '@mui/material';
import { MdCheckBox, MdCheckBoxOutlineBlank, MdOutlineChangeCircle, MdOutlineIndeterminateCheckBox } from 'react-icons/md';
import { FormDialog } from 'components/ui/FormDialog';

import useEnumerationService, { useBasicFilterEnumeration, useEnumerationItemCreateUpdate } from 'features/configuration/services/Enumeration';

import IEnumeration, {
  IEnumerationItem, Enum_STUDY_LEVEL, Enum_STUDY_LANGUAGE,
  Enum_STUDY_TYPE, Enum_STUDY_CYCLE, Enum_PERSON_GENDER,
  Enum_STUDY_SECTION,
  Enum_REGISTRATION_STATUS
} from 'features/configuration/models/Enumeration';


import { isFalsy } from 'utility-types';
import { useLocation, useNavigate } from 'react-router-dom';

import { debounce } from 'lodash';
import { addMonths } from 'date-fns';
import { DatePicker } from '@mui/x-date-pickers';
import { IRegistration, IRegistrationDashboardSearch } from './models/Registration';
import { FaCashRegister } from 'react-icons/fa';
import { Controller, useForm } from 'react-hook-form';
import { GrFormNextLink, GrFormPreviousLink } from 'react-icons/gr';
import { pink } from '@mui/material/colors';


interface ClassInfo {
  id: number;
  classe: string;
  niveau: string;
  section: string;
  cycle: string;
  filles: number;
  garcons: number;
}

export const RegistrationDashboardForm = () => {

  const theme = useTheme();

  const { t, i18n } = useTranslation();

  const navigate = useNavigate();

  const location = useLocation();

  const navigationState = location.state as | { filter?: IRegistrationDashboardSearch; } | undefined;

  const kpiFilter = navigationState?.filter;

  const { openEntityPrintDrawer, openEntityPrintDrawerEx, checkPrintAuthorization,
    hasEntityAuthorization, openEntityActionDrawer } = useEntityService();

  const [currentFormName, setCurrentFormName] = useRecoilState(currentFormNameAtom);
  const [isSaveLoading, setIsSaveLoading] = useRecoilState(isSaveLoadingAtom);

  const { language: lg, schoolYears, applicationSetup, roleEntities } = useRecoilValue(currentUserSessionAtom);
  const [schoolYearName, setSchoolYearName] = useState('');

  const [currentBasicTextFilterProps, setCurrentBasicTextFilterProps] = useRecoilState(currentBasicTextFilterPropsAtom);

  const { height, width } = useWindowSize();

  const { getSchoolYearClasses } = useSchoolYearService();
  const { getRegistrationsDashboard } = useRegistrationService();

  const schoolYearServiceRef = useRef(useSchoolYearService());

  //const [dashboard, setDashboard] = useState<IDashboardSchoolYear>(defaultDashboardSchoolYear);

  const { getEnumerationItemsByEnumerationCodes, getExtensionEnumerationItemsBySchoolYear, getItemName } = useEnumerationService();


  const [openSchoolYearFilter, setOpenSchoolYearFilter] = useState(false);
  const basicFilterSchoolYear = useBasicFilterSchoolYear(
    async (event: React.MouseEvent<unknown>, row: ISchoolYear) => {
      const { id, name } = row;

      setValue('schoolYearId', id);
      setSchoolYearName(name);

      setOpenSchoolYearFilter(false);
    }
  );

  const defaultSearch: IRegistrationDashboardSearch = {
    schoolYearId: applicationSetup.currentSchoolYearId,

    schoolYearClassTemplateId: 0,
    schoolYearClassId: 0,

    order: -1,

    studyType: '',
    studySection: '',
    studyLanguage: '',
    studyCycle: '',
    studyLevel: '',

    gender: '',

    name: '',
    status: '10',

    statusStartDate: addMonths(new Date(), -12),
    statusEndDate: new Date(),

    oldStudent: '',
  };

  const methods = useForm<IRegistrationDashboardSearch>({
    defaultValues: {
      ...defaultSearch,
      ...kpiFilter,
    }
  });
  const { register, setValue, getValues, watch, reset, handleSubmit, control, formState: { errors } } = methods;

  const watchStudyCycle = watch('studyCycle');
  const watchClassOrder = watch('order');

  const watchSchoolYearId = watch('schoolYearId');


  const watchStudyType = watch('studyType');
  const watchStudyLanguage = watch('studyLanguage');
  const watchStudySection = watch('studySection');
  const watchStudyLevel = watch('studyLevel');


  const watchSchoolYearClassTemplateId = watch('schoolYearClassTemplateId');
  const watchSchoolYearClassId = watch('schoolYearClassId');

  const watchGender = watch('gender');
  const watchStatus = watch('status');
  const watchStatusStartDate = watch('statusStartDate');
  const watchStatusEndDate = watch('statusEndDate');

  const watchOldStudent = watch('oldStudent');
  const watchName = watch('name');


  const { data: enumItems, refetch: refetchEnumerationItems } = useQuery<IEnumerationItem[]>(['EnumerationItems', 'SchoolYearClassDashbord'], () => getEnumerationItemsByEnumerationCodes
    ([Enum_STUDY_LEVEL, Enum_STUDY_SECTION, Enum_STUDY_LANGUAGE,
      Enum_STUDY_TYPE, Enum_STUDY_CYCLE, Enum_PERSON_GENDER, Enum_REGISTRATION_STATUS]));


  const [schoolYearClassTemplates, setSchoolYearClassTemplates] = useState<ISchoolYearClassTemplate[]>([]);
  const [classes, setClasses] = useState<ISchoolYearClass[]>([]);


  const handleClickSelectSchoolYear = (event: any) => {
    setOpenSchoolYearFilter(true);
  }

  const [pageSize, setPageSize] = useState<number>(25);
  const [pageNumber, setPageNumber] = useState<number>(1);

  // const [schoolYearClassTemplateId, setSchoolYearClassTemplateId] = useState(0);
  // const [schoolYearClassId, setSchoolYearClassId] = useState(0);

  // const [classOrder, setClassOrder] = React.useState<number>(-1);
  // const [studyType, setStudyType] = React.useState<string>('');
  // const [studySection, setStudySection] = React.useState<string>('');
  // const [studyLanguage, setStudyLanguage] = React.useState<string>('');
  // const [studyCycle, setStudyCycle] = React.useState<string>('');
  // const [studyLevel, setStudyLevel] = React.useState<string>('');
  // const [gender, setGender] = React.useState<string>('');

  // const [name, setName] = React.useState<string>('');
  // const [status, setStatus] = React.useState<string>('');
  // const [statusStartDate, setStatusStartDate] = React.useState<Date>(addMonths(new Date(), -12));
  // const [statusEndDate, setEndStatusDate] = React.useState<Date>(new Date());



  const refPageSize = useRef<number>(25);
  const refPageNumber = useRef<number>(1);

  const refEnumItems = useRef<IEnumerationItem[]>();
  useEffect(() => {
    refEnumItems.current = enumItems;

  }, [enumItems]);


  useEffect(() => {
    refPageSize.current = pageSize;
    refPageNumber.current = pageNumber;

  }, [pageSize, pageNumber]);


  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  // Selection handlers
  const handleSelect = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    setSelectedIds(registrations.map((r) => r.id));
  };

  const handleUnselectAll = () => {
    setSelectedIds([]);
  };

  const handleInvertSelection = () => {
    setSelectedIds((prev) =>
      registrations.map((r) => r.id).filter((id) => !prev.includes(id))
    );
  };

  const [registrations, setRegistrations] = useState<IRegistration[]>([]);

  const handleNavigate2Registration = (registrationId: number) => {
    navigate(`/registration/${registrationId}`); // Adjust path as needed
  };

  const handlePrintRegistration = (registrationId: number) => {
    openEntityPrintDrawer('Registration', registrationId);
  };

  const handleActionRegistration = (registrationId: number) => {
    openEntityActionDrawer('Registration', registrationId);
  };

  const handleNewRegistration = () => {
    navigate(`/registration/0`); // Adjust path as needed
  };

  const debouncedFilterChange = useRef(
    debounce(async (p: IRegistrationDashboardSearch) => {

      const arr = await getRegistrationsDashboard(p, { pageSize: refPageSize.current, pageNumber: refPageNumber.current });
      setRegistrations(arr);
    }, 1000)
  ).current;


  const getRegistrationsFilter = async (pgNumber: number) => {

    setPageNumber(pgNumber);
    const arr = await getRegistrationsDashboard({ ...getValues() }, { pageSize, pageNumber: pgNumber });
    setRegistrations(arr);
  }

  useEffect(() => {
    async function _() {

      debouncedFilterChange({ ...getValues() });
    }
    _();
  }, [watchSchoolYearId, watchStudySection, watchStudyType, watchStudyCycle, watchStudyLanguage, watchStudyLevel,
    watchSchoolYearClassTemplateId, watchSchoolYearClassId,
    watchGender, watchStatus, watchStatusStartDate, watchStatusEndDate,
    watchName, watchOldStudent, pageSize,
    debouncedFilterChange]);


  useEffect(() => {
    async function _() {

      if (watchSchoolYearId <= 0) return;

      const arr = await schoolYearServiceRef.current.getSchoolYearClassTemplates(watchSchoolYearId, false, '', '');
      setSchoolYearClassTemplates(arr);
      //setSchoolYearClassTemplates( arr.filter( x => x.studyLanguage === watchStudyLanguage && x.isActive) );    

      const arr2 = await schoolYearServiceRef.current.getSchoolYearClasses({
        schoolYearId: watchSchoolYearId,
        name: '',
        studyType: '', //watchStudyType, 
        studySection: '',
        studyLanguage: '', //watchStudyLanguage,
        studyLevel: ''
      });

      setClasses(arr2);
    }

    _();

  }, [watchSchoolYearId]);


  useEffect(() => {
    setCurrentFormName(`${t('School year')} - ${t('Student')}`);
    setCurrentBasicTextFilterProps(defaultBasicTextFilterProps);
    setIsSaveLoading(false);
  }, []);

  useEffect(() => {
    async function _() {

      const schoolYear = schoolYears.find(s => s.id === getValues('schoolYearId'));
      setSchoolYearName(schoolYear?.name || '');
      //setSchoolYearId(schoolYear?.id || -1);

      debouncedFilterChange({ ...getValues() });
    }
    _();
  }, []);

  const newData = async (event: MouseEvent<HTMLButtonElement>) => {
    navigate('/registration/0');
  }

  const printData = async (event: MouseEvent<HTMLButtonElement>) => {

    const { schoolYearId,
      schoolYearClassTemplateId, schoolYearClassId,
      studySection, studyLanguage, studyCycle, studyLevel,
      order: classOrder, studyType, gender, name, status, statusStartDate, statusEndDate, oldStudent } = getValues();

    openEntityPrintDrawerEx('RegistrationDashboard', 0,
      [{ name: 'SchoolYearId', value: schoolYearId, dateValue: new Date() },
      { name: 'StudyType', value: studyType, dateValue: new Date() },
      { name: 'ClassOrder', value: classOrder, dateValue: new Date() },
      { name: 'StudyLevel', value: studyLevel, dateValue: new Date() },
      { name: 'StudySection', value: studySection, dateValue: new Date() },
      { name: 'StudyLanguage', value: studyLanguage, dateValue: new Date() },
      { name: 'StudyCycle', value: studyCycle, dateValue: new Date() },
      { name: 'Gender', value: gender, dateValue: new Date() },

      { name: 'Name', value: name, dateValue: new Date() },
      { name: 'StatusStartDate', value: statusStartDate, dateValue: statusStartDate },
      { name: 'StatusEndDate', value: statusEndDate, dateValue: statusEndDate },

      { name: 'PageSize', value: pageSize, dateValue: new Date() },
      { name: 'PageNumber', value: pageNumber, dateValue: new Date() }
      ]);
  }




  return (
    <Box sx={{ mx: 0.1 }}>
      <Grid container rowSpacing={1} columnSpacing={3}>
        <Grid item xs={12}>
          <Stack flexDirection='column'>
            <Box sx={{ ...flexBetween, mt: 0.25, mb: 0.25 }} >
              <Stack direction="row" spacing={1}>
                <Button
                  onClick={handleSelectAll}
                  title={t('Select ALL')}
                  startIcon={<MdCheckBox />}
                  size="small"
                  sx={{ minWidth: 0, px: 1 }}
                >
                  <Typography sx={{ display: { xs: 'none', sm: 'inline' } }}>{t('Select ALL')}</Typography>
                </Button>
                <IconButton onClick={handleUnselectAll} title="Unselect all">
                  <MdCheckBoxOutlineBlank />
                </IconButton>
                <IconButton onClick={handleInvertSelection} title="Invert selection">
                  <MdOutlineIndeterminateCheckBox />
                </IconButton>
              </Stack>
              <Button>
                <Typography variant="h6" id="tableTitle" color="primary" noWrap sx={{ ...typographyGroupBoxStyling }}>
                  {`:::: ${schoolYearName} :::::`}
                </Typography>
                <Box sx={{ ...justifyCenter, ml: 1 }} >
                  {`${t('Click to change school year')}`}
                  <MdOutlineChangeCircle size={24} onClick={handleClickSelectSchoolYear} />
                </Box>
              </Button>

              <Stack direction="row" spacing={1}>
                {roleEntities.some(re => re.entityName === 'Registration') &&
                  <Button
                    color="success"
                    variant="contained"
                    startIcon={<FaCashRegister />}
                    //disabled={selectedIds.length === 0}
                    onClick={handleNewRegistration}
                  >
                    {t('New registration')}
                  </Button>}

              </Stack>

              <Button id='btnPrint' onClick={printData} sx={{ display: 'none' }} />
              <Button id='btnNew' onClick={newData} sx={{ display: 'none' }} />

            </Box>
            {openSchoolYearFilter && <FormDialog open={openSchoolYearFilter} maxWidth='md'
              okText='' cancelText='' title={t('School year')} onCancel={() => { }}
              onClose={() => { setOpenSchoolYearFilter(false); }} onOk={() => { setOpenSchoolYearFilter(false); }}  >
              <BasicTextFilterForm<ISchoolYear> {...basicFilterSchoolYear} />
            </FormDialog>}
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <Stack flexDirection='column'>
            <Box sx={{
              mt: 1, width: '100%',
              display: 'grid', gap: 1.5, // spacing between items (adjust as needed)
              gridTemplateColumns: {
                xs: 'repeat(2, 1fr)',  // 2 per row on extra-small screens (sx)
                sm: 'repeat(3, 1fr)',  // 3 per row on small screens (sm)
                md: 'repeat(7, 1fr)',  // 6 per row on medium screens (md) and up
              },
            }} >
              <Controller name='studySection' control={control}
                render={({ field }) => (
                  <TextField select sx={{ width: 'calc(100% - 8px)' }}  {...field} id="studySection"
                    label={t('Study section')} inputProps={{ readOnly: false }} focused >
                    <MenuItem value=''>{':::'}</MenuItem>
                    {enumItems && enumItems.filter(e =>
                      e.enumerationCode === Enum_STUDY_SECTION).map(
                        (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                    }
                  </TextField>)}
              />
              <Controller name='studyLanguage' control={control}
                render={({ field }) => (
                  <TextField select sx={{ width: 'calc(100% - 8px)' }}  {...field} id="studyLanguage"
                    label={t('Study language')} inputProps={{ readOnly: false }} focused >
                    <MenuItem value=''>{':::'}</MenuItem>
                    {enumItems && enumItems.filter(e =>
                      e.enumerationCode === Enum_STUDY_LANGUAGE).map(
                        (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                    }
                  </TextField>)}
              />
              <Controller name='studyCycle' control={control}
                render={({ field }) => (
                  <TextField select sx={{ width: 'calc(100% - 8px)' }}  {...field} id="studyCycle"
                    label={t('Cycle')} inputProps={{ readOnly: false }} focused >
                    <MenuItem value=''>{':::'}</MenuItem>
                    {enumItems && enumItems.filter(e =>
                      e.enumerationCode === Enum_STUDY_CYCLE).map(
                        (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                    }
                  </TextField>)}
              />
              <Controller name='studyLevel' control={control}
                render={({ field }) => (
                  <TextField select sx={{ width: 'calc(100% - 8px)' }}  {...field} id="studyLevel"
                    label={t('Study level')} inputProps={{ readOnly: false }} focused >
                    <MenuItem value=''>{':::'}</MenuItem>
                    {enumItems && enumItems.filter(e =>
                      e.enumerationCode === Enum_STUDY_LEVEL).map(
                        (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                    }
                  </TextField>)}
              />
              {/* <Controller name='order' control={control}
                render={({ field }) => (
                  <TextField select sx={{ width: 'calc(100% - 8px)' }}  {...field} id="classOrder"
                    label={t('Order')} inputProps={{ readOnly: false }} focused >
                    <MenuItem value={0}>{':::'}</MenuItem>
                    {Array.from({ length: 17 }, (_, i) => i + 1).map(
                      (x, idx) => <MenuItem key={x} value={x}>{x}</MenuItem>)
                    }
                  </TextField>)}
              /> */}
              <Controller name='studyType' control={control}
                render={({ field }) => (
                  <TextField select sx={{ width: 'calc(100% - 8px)' }}  {...field} id="studyType"
                    label={t('Study type')} inputProps={{ readOnly: false }} focused >
                    <MenuItem value=''>{':::'}</MenuItem>
                    {enumItems && enumItems.filter(e =>
                      e.enumerationCode === Enum_STUDY_TYPE).map(
                        (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                    }
                  </TextField>)}
              />
              <Controller name='schoolYearClassTemplateId' control={control}
                render={({ field: { onChange, value } }) => (
                  <TextField select onChange={onChange} value={value} sx={{ width: `calc(100% - 8px)` }} id="schoolYearClassTemplateId"
                    label={`${t('Class')} - ${t('Study level')}`} inputProps={{ readOnly: false }} focused >
                    {schoolYearClassTemplates && schoolYearClassTemplates
                      .filter(x => x.studyLanguage.includes(watchStudyLanguage) &&
                        x.studySection.includes(watchStudySection) &&
                        x.studyLevel.includes(watchStudyLevel)).map(
                          (x, idx) => <MenuItem key={x.id} value={x.id}>{x.classTemplateName}</MenuItem>)
                    }
                  </TextField>
                )}
              />
              <Controller name='schoolYearClassId' control={control}
                render={({ field: { onChange, value } }) => (
                  <TextField select onChange={onChange} value={value} sx={{ width: `calc(100% - 8px)` }} id="schoolYearClassId"
                    label={t('Class')} inputProps={{ readOnly: false }}
                  >
                    {classes && classes.filter(x => x.studyLanguage.includes(watchStudyLanguage) && x.studySection.includes(watchStudySection) &&
                      x.studyLevel.includes(watchStudyLevel) &&
                      (x.schoolYearClassTemplateId === watchSchoolYearClassTemplateId || watchSchoolYearClassTemplateId <= 0)).map(
                        (x, idx) => <MenuItem key={x.id} value={x.id}>{x.name}</MenuItem>)
                    }
                  </TextField>
                )}
              />


              <Controller name='status' control={control}
                render={({ field }) => (
                  <TextField select sx={{ width: 'calc(100% - 8px)' }}  {...field} id="status"
                    label={t('Status')} inputProps={{ readOnly: false }} focused >
                    <MenuItem value=''>{':::'}</MenuItem>
                    {enumItems && enumItems.filter(e =>
                      e.enumerationCode === Enum_REGISTRATION_STATUS).map(
                        (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                    }
                  </TextField>
                )}
              />
              <Controller name='statusStartDate' control={control}
                render={({ field: { onChange, value } }) => (
                  <DatePicker label={t('Start date')}
                    onChange={onChange}
                    value={new Date(value)}
                    slotProps={{ textField: { sx: { width: 'calc(100% - 8px)' } } }}
                  //renderInput={(params) => <TextField {...params} sx={{width:'calc(50% - 8px)'}} />}
                  />)}
              />
              <Controller name='statusEndDate' control={control}
                render={({ field: { onChange, value } }) => (
                  <DatePicker label={t('End date')}
                    onChange={onChange}
                    value={new Date(value)}
                    slotProps={{ textField: { sx: { width: 'calc(100% - 8px)' } } }}
                  //renderInput={(params) => <TextField {...params} sx={{width:'calc(50% - 8px)'}} />}
                  />)}
              />
              <Controller name='gender' control={control}
                render={({ field }) => (
                  <TextField select sx={{ width: 'calc(100% - 8px)' }}  {...field} id="gender"
                    label={t('Gender')} inputProps={{ readOnly: false }} focused >
                    <MenuItem value=''>{':::'}</MenuItem>
                    {enumItems && enumItems.filter(e =>
                      e.enumerationCode === Enum_PERSON_GENDER).map(
                        (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                    }
                  </TextField>)}
              />
              <Controller name='name' control={control}
                render={({ field }) => (
                  <TextField sx={{ width: 'calc(100% - 8px)' }}  {...field} id="name" label={`${t('Name')}`}
                    inputProps={{ autoComplete: 'new-password', style: { textTransform: 'none' } }} />
                )}
              />
              <Controller name='oldStudent' control={control}
                render={({ field }) => (
                  <TextField select sx={{ width: 'calc(100% - 8px)' }}  {...field} id="gender"
                    label={t('Old ?')} inputProps={{ readOnly: false }} focused >
                    <MenuItem value=''>{':::'}</MenuItem>
                    <MenuItem value='Y'>{t('Olds')}</MenuItem>
                    <MenuItem value='N'>{t('News')}</MenuItem>
                  </TextField>)}
              />
              <TextField select onChange={(evt) => setPageSize(Number(evt.target.value))}
                value={pageSize} sx={{ width: 'calc(100% - 8px)' }} id="pageSize"
                label={t('Page size')} inputProps={{ readOnly: false }} focused >
                <MenuItem value={5}>5</MenuItem>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </TextField>

            </Box>
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <Stack flexDirection='column'>
            <Box p={2}>
              <Grid container spacing={2}>
                {registrations//.filter(x => x.studyCycle.includes(watchStudyCycle) && (watchClassOrder <= 0 || x.classOrder === watchClassOrder))
                  .map(({ id, firstName, lastName, studyLanguage, schoolYearClassName, fullNameFR, fullNameEN,
                    studyCycle, classOrder }) => {

                    return (
                      <Grid key={id} item xs={12} sm={6} md={4} lg={3} xl={3} >
                        <Card sx={{ height: '100%', border: '1px solid #ccc', borderRadius: 2, boxShadow: 2, padding: 2, }}>
                          <CardContent sx={{ p: 1.5 }}>
                            <Box display="flex" justifyContent="space-between">

                              <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Checkbox
                                  checked={selectedIds.includes(id)}
                                  onChange={() => handleSelect(id)}
                                  color="primary"
                                />
                                <Typography sx={{ ml: 1, flex: 1, textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                  variant="subtitle2" noWrap>
                                  {lg.includes('fr') ? fullNameFR : fullNameEN}
                                </Typography>
                              </Box>

                            </Box>
                            <Typography variant="body2" gutterBottom>
                              [{schoolYearClassName}] - {getItemName(enumItems, Enum_STUDY_LANGUAGE, studyLanguage)}
                            </Typography>
                            {/* <Typography variant="body2" gutterBottom>
                              {countGirls} {t('Girls')}, {countBoys} {t('Boys')}, TOTAL: {countStudents}
                            </Typography> */}
                            <Box mt={1} display="flex" justifyContent="space-around">
                              <IconButton color="primary" disabled={!hasEntityAuthorization('Registration')} onClick={() => handleNavigate2Registration(id)} aria-label="liste des élèves" >
                                <VisibilityIcon />
                              </IconButton>
                              <IconButton color="secondary" disabled={!checkPrintAuthorization('Registration')} onClick={() => handlePrintRegistration(id)} aria-label="imprimer">
                                <PrintIcon />
                              </IconButton>
                              <IconButton color="default" disabled={!hasEntityAuthorization('Registration')} onClick={() => handleActionRegistration(id)} aria-label="actions" >
                                <SystemUpdateAltIcon />
                              </IconButton>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
              </Grid>

            </Box>
            <Box sx={{ mt: 1, mb: 1, width: '100%', display: 'flex', justifyContent: 'center' }} >
              {pageNumber > 1 && <Button variant="outlined" onClick={() => getRegistrationsFilter(pageNumber - 1)}
                id="btnBasicTextFilter" sx={{ ml: 1, mr: 1 }}>
                <Box sx={{ ml: 1, mr: 1, mt: 0.5, }} >
                  <GrFormPreviousLink color={pink[500]} size={16} />
                </Box>
                <Typography
                  sx={{
                    color: (theme) => theme.palette.text.primary,
                    fontWeight: 'bold',
                    display: { xs: 'none', md: 'block' }
                  }} >
                  {t('Previous')}
                </Typography>

              </Button>}
              <Typography
                sx={{
                  color: (theme) => theme.palette.text.primary,
                  fontWeight: 'bold',
                  display: { xs: 'none', md: 'block' }
                }} >
                {`${registrations.length} ${t('Item')}(s)`}
              </Typography>
              {registrations.length >= pageSize && <Button variant="outlined" onClick={() => getRegistrationsFilter(pageNumber + 1)}
                id="btnBasicTextFilter" sx={{ ml: 1, mr: 1 }}>
                <Typography
                  sx={{
                    color: (theme) => theme.palette.text.primary,
                    fontWeight: 'bold',
                    display: { xs: 'none', md: 'block' }
                  }} >
                  {t('Next')}
                </Typography>
                <Box sx={{ ml: 1, mr: 1, mt: 0.5, }} >
                  <GrFormNextLink color={pink[500]} size={16} />
                </Box>
              </Button>}
            </Box>
          </Stack>
        </Grid>
      </Grid>

    </Box>
  )
}
